import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { isValidContact } from '../../entities/Contact';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
const CONTACT_FIELD_SET = 'COMMUNICATION_DETAILS';
const CONTACTS_COUNT_LIMIT = 10;
export class ContactClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "createContactImpl", void 0);
    _defineProperty(this, "getContactImpl", void 0);
    _defineProperty(this, "queryContactsImpl", void 0);
    _defineProperty(this, "searchContactsImpl", void 0);
    _defineProperty(this, "getInboxConversationIdImpl", void 0);
  }
  getServiceNameForLogs() {
    return 'ContactClient';
  }
  async createContact(contactInfo, allowDuplicates) {
    try {
      const {
        firstName,
        lastName,
        email,
        phone
      } = contactInfo;
      const request = {
        info: {
          name: {
            first: firstName,
            last: lastName
          },
          emails: {
            items: [{
              email,
              primary: true
            }]
          }
        },
        allowDuplicates
      };
      if (phone) {
        request.info.phones = {
          items: [{
            phone: contactInfo.phone,
            e164Phone: contactInfo.phone
          }]
        };
      }
      for (var _len = arguments.length, extraParam = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        extraParam[_key - 2] = arguments[_key];
      }
      const {
        contact
      } = await this.request(this.createContactImpl(request), ...extraParam);
      if (!contact) {
        throw Error('Invalid contact response');
      }
      return this.convertToEntity(contact);
    } catch (error) {
      this.logError('createContact', 'Failed to create contact', {
        contactInfo
      }, error);
      throw error;
    }
  }
  async getContact(contactId) {
    try {
      for (var _len2 = arguments.length, extraParam = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        extraParam[_key2 - 1] = arguments[_key2];
      }
      const {
        contact
      } = await this.request(this.getContactImpl({
        id: contactId
      }), ...extraParam);
      return this.convertToEntity(contact);
    } catch (error) {
      this.logError('getContacts', 'Failed to get contact', {
        contactId
      }, error);
      throw error;
    }
  }
  async getContacts(contactIds) {
    try {
      for (var _len3 = arguments.length, extraParam = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        extraParam[_key3 - 1] = arguments[_key3];
      }
      const {
        contacts
      } = await this.request(this.queryContactsImpl({
        query: {
          filter: {
            id: {
              $in: contactIds
            }
          }
        }
      }), ...extraParam);
      if (!contacts) {
        throw new Error('Received invalid contacts');
      }
      if (contacts.length !== contactIds.length) {
        const contactsNotFound = contactIds.filter(id => !contacts.find(contact => contact.id === id));
        throw new Error('Some contacts were not found', {
          cause: {
            contactsNotFound
          }
        });
      }
      return contacts.map(this.convertToEntity);
    } catch (error) {
      this.logError('getContacts', 'Failed to get contacts', {
        contactIds
      }, error);
      throw error;
    }
  }
  async searchContactsByKeyword(keyword) {
    try {
      for (var _len4 = arguments.length, extraParam = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        extraParam[_key4 - 1] = arguments[_key4];
      }
      const {
        items: nonFuzzySearchItems,
        hasNext: nonFuzzySearchHasNext
      } = await this.searchContacts(this.buildSearchContactsRequestData(keyword, false), ...extraParam);
      if (nonFuzzySearchItems.length > 0) {
        if (nonFuzzySearchHasNext) {
          this.logError('searchContactsByKeyword', 'Non fuzzy search returned too many contacts', {
            keyword
          });
        }
        return nonFuzzySearchItems;
      }
      const {
        items: fuzzySearchItems,
        hasNext: fuzzySearchHasNext
      } = await this.searchContacts(this.buildSearchContactsRequestData(keyword, true), ...extraParam);
      if (fuzzySearchHasNext) {
        this.logError('searchContactsByKeyword', 'Non fuzzy search returned too many contacts', {
          keyword
        });
      }
      return fuzzySearchItems;
    } catch (error) {
      this.logError('searchContactsByKeyword', 'Failed to search contacts', {
        keyword
      }, error);
      throw error;
    }
  }
  async searchContacts(search) {
    try {
      for (var _len5 = arguments.length, extraParam = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        extraParam[_key5 - 1] = arguments[_key5];
      }
      const {
        contacts,
        cursorPagingMetadata
      } = await this.request(this.searchContactsImpl({
        search
      }), ...extraParam);
      if (!contacts) {
        throw new Error('Received invalid contacts');
      }
      return {
        items: contacts.map(this.convertToEntity),
        ...pagingMetadataToCursorResponse(cursorPagingMetadata)
      };
    } catch (error) {
      this.logError('searchContacts', 'Failed to search contacts', {
        search
      }, error);
      throw error;
    }
  }
  async getInboxConversationId(contactId) {
    try {
      for (var _len6 = arguments.length, extraParam = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        extraParam[_key6 - 1] = arguments[_key6];
      }
      const {
        conversation
      } = await this.request(this.getInboxConversationIdImpl({
        participantId: {
          contactId
        }
      }), ...extraParam);
      if (!conversation) {
        throw new Error('Invalid inbox conversation id');
      }
      return conversation.id;
    } catch (error) {
      this.logError('getInboxConversationId', 'Failed to get inbox conversation id', {
        contactId
      }, error);
      throw error;
    }
  }
  buildSearchContactsRequestData(expression, fuzzy) {
    return {
      fieldsets: [CONTACT_FIELD_SET],
      search: {
        expression,
        fuzzy
      },
      cursorPaging: {
        limit: CONTACTS_COUNT_LIMIT
      }
    };
  }
  convertToEntity(contact) {
    try {
      var _info$name, _info$name2, _info$emails, _info$phones, _info$picture, _memberInfo$userInfo;
      if (!contact) {
        throw new Error('Invalid contact', {
          cause: {
            contact
          }
        });
      }
      const {
        id,
        info,
        revision,
        primaryEmail,
        primaryPhone,
        memberInfo
      } = contact;
      const convertedContact = {
        id,
        revision,
        firstName: (info == null || (_info$name = info.name) == null ? void 0 : _info$name.first) ?? undefined,
        lastName: (info == null || (_info$name2 = info.name) == null ? void 0 : _info$name2.last) ?? undefined,
        primaryEmail: (primaryEmail == null ? void 0 : primaryEmail.email) ?? undefined,
        primaryPhone: (primaryPhone == null ? void 0 : primaryPhone.formattedPhone) ?? undefined,
        e164Phone: (primaryPhone == null ? void 0 : primaryPhone.e164Phone) ?? undefined,
        emails: info == null || (_info$emails = info.emails) == null || (_info$emails = _info$emails.items) == null ? void 0 : _info$emails.map(_ref => {
          let {
            email
          } = _ref;
          return email;
        }),
        phones: info == null || (_info$phones = info.phones) == null || (_info$phones = _info$phones.items) == null ? void 0 : _info$phones.map(_ref2 => {
          let {
            phone
          } = _ref2;
          return phone;
        }),
        imageUrl: info == null || (_info$picture = info.picture) == null || (_info$picture = _info$picture.image) == null ? void 0 : _info$picture.url,
        userId: memberInfo == null || (_memberInfo$userInfo = memberInfo.userInfo) == null ? void 0 : _memberInfo$userInfo.userId
      };
      if (isValidContact(convertedContact)) {
        return convertedContact;
      } else {
        throw new Error('Invalid Contact data', {
          cause: {
            contact
          }
        });
      }
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert Contact', {
        contact
      }, error);
      throw error;
    }
  }
}
//# sourceMappingURL=ContactClientBase.js.map